import { useReadOnly } from '../../Hooks/useReadOnly';
import { Permissions } from '../../Security/permissions';
import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Button, Form, Card } from 'react-bootstrap';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { AppLogoDataService } from '../../Services/AppLogoDataService';
import ConfirmationModal from '../../Components/ConfirmationModal/ConfirmationModal';

const AppLogoConfiguration = () => {
    const readOnly = useReadOnly(Permissions.Security.Configuration);
    const { instance, accounts, inProgress } = useMsal();
    const [logoUri, setLogoUri] = useState('');
    const [newLogo, setNewLogo] = useState(null);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const fileInputRef = useRef(null); // Reference to the file input for clearing

    useEffect(() => {
        if (inProgress === "none" && accounts.length > 0) {
            fetchAppLogo();
        }
    }, [accounts, inProgress]);

    const fetchAppLogo = async () => {
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);
            const data = await AppLogoDataService.getAppLogo(response.accessToken);
            setLogoUri(data.logoUri);
        } catch (error) {
            console.error('Failed to fetch app logo:', error);
        }
    };

    const handleFileChange = (e) => {
        setNewLogo(e.target.files[0]);
    };

    const handleSaveLogo = async () => {
        if (readOnly) { return; }
        if (!newLogo) {
            alert('Please select a file first.');
            return;
        }

        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);

            const formData = new FormData();
            formData.append('file', newLogo);

            await AppLogoDataService.updateAppLogo(formData, response.accessToken);
            fetchAppLogo(); // Refresh the logo after saving
            setNewLogo(null); // Clear the newLogo state
            if (fileInputRef.current) fileInputRef.current.value = ''; // Clear the file input
        } catch (error) {
            console.error('Failed to update app logo:', error);
        }
    };

    const handleRemoveLogoConfirmation = async () => {
        if (readOnly) { return; }
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);

            await AppLogoDataService.removeAppLogo(response.accessToken);
            setLogoUri(''); // Set to default or empty URI
            setShowConfirmationModal(false);
        } catch (error) {
            console.error('Failed to remove app logo:', error);
        }
    };

    const handleRemoveLogoButton = async () => {
        setShowConfirmationModal(true);
    }

    return (
        <Container className='mx-4'>

            <Row className='justify-content-center'>
                <Col md={10}>
                    <h4 className='text-left mb-3'>App Logo Configuration</h4>
                    <Card className='w-100 mb-4 current-logo-card'>
                        {logoUri ? (
                            <>
                                <Card.Img variant="top" src={logoUri} />
                                <Card.Body className='text-center'>
                                    <div className='d-flex justify-content-center align-items-center'>
                                        <span><strong>Current logo: </strong>{logoUri.split('/').pop().split('?')[0]}</span>
                                        <Button disabled={readOnly} variant="outline-danger" className="ms-4" onClick={handleRemoveLogoButton}>
                                            Remove
                                        </Button>
                                    </div>
                                </Card.Body>
                            </>
                        ) : (
                            <Card.Body>
                                <span>No logo uploaded. Default logo will be used in the app landing page.</span>
                            </Card.Body>
                        )}
                    </Card>
                    <Form.Group controlId="formFile" as={Row}>
                        <Form.Label column md={4}>Upload a new logo (recommended size: 600x200 or any size with a similar aspect ratio)</Form.Label>
                        <Col md={8}>
                            <Form.Control disabled={readOnly} type="file" ref={fileInputRef} onChange={handleFileChange} />
                        </Col>
                    </Form.Group>
                    <div className='d-flex justify-content-end'>
                        <Button disabled={readOnly} className="mt-3 px-4" onClick={handleSaveLogo}>Save</Button>
                    </div>
                </Col>
            </Row>
            <ConfirmationModal
                show={showConfirmationModal}
                onHide={() => setShowConfirmationModal(false)}
                onCancelClick={() => setShowConfirmationModal(false)}
                onAcceptClick={handleRemoveLogoConfirmation}
                title='Remove current logo'
                content={<span>You are about to delete the current logo from the system and this operation can't be reversed. Do you want to continue?</span>}
            />
        </Container>
    );
};

export default AppLogoConfiguration;
